import { useState, useEffect } from 'react'
import { useAuth } from '../../context/AuthContext'
import { useLanguage } from '../../context/LanguageContext'
import { AppLayout } from '../../components/layouts/AppLayout'
import { MetricCard } from '../../components/dashboard/MetricCard'
import { Button } from '../../components/ui/button'
import {
  Buildings,
  Users,
  Package,
  Briefcase,
  Plus,
  ChartLine,
  CheckCircle,
  WarningCircle,
  Spinner,
  UserSwitch
} from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'
import api from '../../services/api'

interface DashboardStats {
  totalTenants: number
  tenantsThisMonth: number
  totalUsers: number
  usersThisMonth: number
  totalCompanies: number
  totalQuotes: number
  quotesThisMonth: number
}

interface RecentTenant {
  id: string
  name: string
  createdAt: string
  companiesCount: number
  usersCount: number
}

export default function MasterDashboard() {
  const { user, startImpersonation } = useAuth()
  const { t } = useLanguage()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [impersonateEmail, setImpersonateEmail] = useState('')
  const [impersonateReason, setImpersonateReason] = useState('')
  const [impersonating, setImpersonating] = useState(false)
  const [impersonateError, setImpersonateError] = useState<string | null>(null)

  async function handleQuickImpersonate(e: React.FormEvent) {
    e.preventDefault()
    if (!impersonateEmail) return
    setImpersonateError(null)
    setImpersonating(true)
    try {
      await startImpersonation({
        targetEmail: impersonateEmail.trim(),
        reason: impersonateReason.trim() || undefined
      })
      navigate('/dashboard', { replace: true })
    } catch (err: any) {
      setImpersonateError(err.response?.data?.error || err.message || 'Falha ao impersonar')
    } finally {
      setImpersonating(false)
    }
  }
  const [recentTenants, setRecentTenants] = useState<RecentTenant[]>([])
  const [error, setError] = useState<string | null>(null)

  const systemHealth = {
    database: 'healthy' as const,
    api: 'healthy' as const,
    email: 'healthy' as const,
    storage: 'healthy' as const,
  }

  useEffect(() => {
    async function fetchStats() {
      try {
        setLoading(true)
        const { data } = await api.get('/master/stats')
        setStats(data.stats)
        setRecentTenants(data.recentTenants)
        setError(null)
      } catch (err: any) {
        console.error('Error fetching stats:', err)
        setError(err.response?.data?.error || 'Erro ao carregar estatísticas')
      } finally {
        setLoading(false)
      }
    }

    fetchStats()
  }, [])

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Painel Master
              </h1>
              <p className="text-sm text-gray-500">
                Bem-vindo, {user?.name}
              </p>
            </div>
            <Button
              onClick={() => navigate('/master/tenants/new')}
              size="lg"
              className="gap-2"
            >
              <Plus size={20} />
              Novo Tenant
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Impersonate */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <UserSwitch size={20} className="text-red-600" weight="duotone" />
            <h3 className="text-sm font-semibold text-gray-900">Impersonar usuário (suporte)</h3>
          </div>
          <form onSubmit={handleQuickImpersonate} className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs text-gray-600 mb-1">Email do alvo</label>
              <input
                type="email"
                value={impersonateEmail}
                onChange={(e) => setImpersonateEmail(e.target.value)}
                placeholder="usuario@exemplo.com"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs text-gray-600 mb-1">Motivo (opcional)</label>
              <input
                type="text"
                value={impersonateReason}
                onChange={(e) => setImpersonateReason(e.target.value)}
                placeholder="Ticket #1234 — investigar quote duplicada"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
            <Button
              type="submit"
              disabled={impersonating || !impersonateEmail}
              className="bg-red-600 hover:bg-red-700 text-white gap-2"
            >
              <UserSwitch size={16} />
              {impersonating ? 'Entrando...' : 'Entrar como'}
            </Button>
          </form>
          {impersonateError && (
            <p className="mt-2 text-xs text-red-600">{impersonateError}</p>
          )}
          <p className="mt-2 text-xs text-gray-500">
            Sessão expira em 1h. Toda impersonação fica registrada no audit log.
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Spinner size={32} className="animate-spin text-emerald-600" />
            <span className="ml-3 text-gray-500">Carregando estatísticas...</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-600">{error}</p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => window.location.reload()}
            >
              Tentar novamente
            </Button>
          </div>
        )}

        {/* Metrics Grid */}
        {stats && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <MetricCard
                title="Total de Tenants"
                value={stats.totalTenants}
                subtitle={`+${stats.tenantsThisMonth} este mês`}
                icon={Buildings}
                iconColor="rgb(5, 150, 105)"
              />
              <MetricCard
                title="Usuários"
                value={stats.totalUsers}
                subtitle={`+${stats.usersThisMonth} este mês`}
                icon={Users}
                iconColor="rgb(59, 130, 246)"
              />
              <MetricCard
                title="Empresas"
                value={stats.totalCompanies}
                subtitle="Shippers + Agentes"
                icon={Briefcase}
                iconColor="rgb(139, 92, 246)"
              />
              <MetricCard
                title="Cotações"
                value={stats.totalQuotes}
                subtitle={`+${stats.quotesThisMonth} este mês`}
                icon={Package}
                iconColor="rgb(251, 146, 60)"
              />
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Recent Tenants Section */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        Tenants Recentes
                      </h2>
                      <p className="text-sm text-gray-500 mt-1">
                        Últimos tenants cadastrados
                      </p>
                    </div>
                    <Button variant="outline" onClick={() => navigate('/master/tenants')}>
                      Ver Todos
                    </Button>
                  </div>
                </div>
                <div className="p-6">
                  {recentTenants.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">
                      Nenhum tenant cadastrado ainda
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {recentTenants.map((tenant) => (
                        <div
                          key={tenant.id}
                          className="flex items-center justify-between p-3 rounded-lg border border-gray-100 hover:border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => navigate(`/master/tenants/${tenant.id}`)}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                              <Buildings size={20} className="text-emerald-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{tenant.name}</p>
                              <p className="text-xs text-gray-500">
                                {new Date(tenant.createdAt).toLocaleDateString('pt-BR')}
                              </p>
                            </div>
                          </div>
                          <div className="text-right text-sm">
                            <p className="text-gray-600">{tenant.companiesCount} empresas</p>
                            <p className="text-gray-400">{tenant.usersCount} usuários</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* System Health Section */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                <div className="px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">
                        Saúde do Sistema
                      </h2>
                      <p className="text-sm text-gray-500 mt-1">
                        Status dos principais serviços
                      </p>
                    </div>
                    <Button variant="outline" onClick={() => navigate('/master/settings')}>
                      <ChartLine size={16} className="mr-2" />
                      Configurações
                    </Button>
                  </div>
                </div>
                <div className="p-6">
                  <div className="space-y-4">
                    <HealthIndicator label="Banco de Dados" status={systemHealth.database} />
                    <HealthIndicator label="API" status={systemHealth.api} />
                    <HealthIndicator label="Serviço de Email" status={systemHealth.email} />
                    <HealthIndicator label="Armazenamento" status={systemHealth.storage} />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </main>
    </AppLayout>
  )
}

// Helper component for system health indicators
interface HealthIndicatorProps {
  label: string
  status: 'healthy' | 'warning' | 'error'
}

function HealthIndicator({ label, status }: HealthIndicatorProps) {
  const getStatusConfig = () => {
    switch (status) {
      case 'healthy':
        return {
          icon: CheckCircle,
          color: 'rgb(5, 150, 105)',
          bgColor: 'rgba(5, 150, 105, 0.1)',
          text: 'Operacional',
        }
      case 'warning':
        return {
          icon: WarningCircle,
          color: 'rgb(251, 146, 60)',
          bgColor: 'rgb(251, 146, 60, 0.1)',
          text: 'Atenção',
        }
      case 'error':
        return {
          icon: WarningCircle,
          color: 'rgb(239, 68, 68)',
          bgColor: 'rgb(239, 68, 68, 0.1)',
          text: 'Problema',
        }
    }
  }

  const config = getStatusConfig()
  const Icon = config.icon

  return (
    <div className="flex items-center justify-between p-4 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors">
      <div className="flex items-center gap-3">
        <div
          className="rounded-lg p-2"
          style={{ backgroundColor: config.bgColor }}
        >
          <Icon size={20} style={{ color: config.color }} />
        </div>
        <span className="text-sm font-medium text-gray-700">{label}</span>
      </div>
      <span
        className="text-sm font-medium"
        style={{ color: config.color }}
      >
        {config.text}
      </span>
    </div>
  )
}
